<script setup>
// Collapsed "Check-in details" trigger row + always-visible blackout banner.
// Port of `DetailsPanel` in the source `_shared/CheckInSheet.tsx`.
import { computed } from 'vue'
import { AlertTriangle, Info, ChevronDown } from '@lucide/vue'
import { hasDetailsContent } from './checkin'

const props = defineProps({
  details: { type: Object, default: null },
})

defineEmits(['open'])

const hasContent = computed(() => hasDetailsContent(props.details))
const visible = computed(() => props.details && (hasContent.value || props.details.isBlackoutToday))
</script>

<template>
  <div v-if="visible" class="cs-details-panel">
    <div v-if="details.isBlackoutToday" class="cs-details-panel__blackout">
      <AlertTriangle class="cs-details-panel__blackout-icon" :size="14" />
      <div class="cs-details-panel__blackout-text">
        <span class="cs-details-panel__blackout-label">Blackout today</span>
        <span class="cs-details-panel__blackout-sub">— check-in isn't available today.</span>
      </div>
    </div>

    <button
      v-if="hasContent"
      id="checkin-details-open"
      type="button"
      class="cs-details-panel__trigger gtm-click"
      @click="$emit('open')"
    >
      <span class="cs-details-panel__trigger-left">
        <Info class="cs-details-panel__info" :size="14" />
        <span class="cs-details-panel__trigger-label">Check-in details</span>
      </span>
      <ChevronDown class="cs-details-panel__chevron" :size="14" />
    </button>
  </div>
</template>

<style lang="scss" scoped>
@use './checkin-shared' as *;

// Source: `mb-2 space-y-2`
.cs-details-panel {
  margin-bottom: 8px; // mb-2

  > * + * {
    margin-top: 8px; // space-y-2
  }

  // Source: `flex items-start gap-2 rounded-xl bg-rose-500/10 px-3 py-2
  //          text-rose-600 ring-1 ring-rose-500/30`
  &__blackout {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    border-radius: 12px;
    background: rgba($cs-rose-500, 0.1);
    padding: 8px 12px;
    color: $cs-rose-600;
    box-shadow: 0 0 0 1px rgba($cs-rose-500, 0.3);
  }

  &__blackout-icon {
    margin-top: 2px; // mt-0.5
    flex-shrink: 0;
  }

  &__blackout-text {
    font-size: 11px;
    line-height: 1.375; // leading-snug
  }

  &__blackout-label {
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  &__blackout-sub {
    margin-left: 4px; // ml-1
    opacity: 0.9;
  }

  // Source: `flex w-full items-center justify-between gap-2 rounded-xl
  //          bg-current/[0.04] px-3 py-2 text-left ring-1 ring-current/10 outline-none`
  &__trigger {
    @include cs-button-reset;
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    border-radius: 12px;
    background: cs-current(4%);
    padding: 8px 12px;
    text-align: left;
    box-shadow: 0 0 0 1px cs-current(10%);
    outline: none;

    &:focus,
    &:focus-visible {
      outline: none;
    }
  }

  &__trigger-left {
    display: inline-flex;
    align-items: center;
    gap: 8px;
  }

  &__info {
    color: var(--fr-accent, var(--fr-primary, #FF9900));
    flex-shrink: 0;
  }

  // Source: `font-display text-[11px] font-bold uppercase tracking-wider`
  &__trigger-label {
    @include cs-overline(11px);
  }

  // Source: `h-3.5 w-3.5 -rotate-90 ${theme.muted}`
  &__chevron {
    transform: rotate(-90deg);
    color: var(--fr-muted, #6b7280);
    flex-shrink: 0;
  }
}
</style>
